/**
 * Bloomin REST PG — 자체 카드 입력 form (#1413 PR-5 skeleton).
 *
 * Bloomin/OpenLinea 3DS 결제 카드 입력 form.
 *
 * ## PCI DSS 정합
 * - PAN/CVV 는 BE → Bloomin REST `/api/tarjetas` 토큰화 1회 처리
 * - FE 메모리 즉시 폐기 (form 제출 후 useState 초기화)
 * - HTTPS 강제 (admin BE endpoint TLS 1.2+)
 *
 * ## 통합 흐름 (Option D)
 *   1. 사용자 카드 입력 → 본 form
 *   2. POST {VITE_API_ENDPOINT}{initiateUrl}
 *      → admin BE BloominGateway (crearCliente + crearTarjeta + iniciar3DS)
 *   3. 응답 challengeUrl 받아 redirect (3DS 2.0)
 *   4. 발급은행 인증 후 → admin BE callback → tokenVenta SOAP (PR-5/PR-6 통합)
 *   5. 결제 결과 → 영수증 페이지
 *
 * ## ⚠️ skeleton 단계 (OEL 영업 자격증명 + admin BE staging E2E 검증 의존)
 * - admin BE initiate endpoint 가 반환한 challengeUrl 로 3DS redirect 수행
 *
 * @see admin#1413 (메타) / admin#1414 (BE Umbrella) / menu#427 (FE Umbrella)
 */
import { useState, useCallback, FormEvent } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { logger } from '@/utils/logger';
import { getApiErrorLanguage, getGenericFallbackMessage, getStatusMessage } from '@/lib/api/error-messages';
import { BloominInitiateError, readBloominInitiateResponse } from './bloominInitiateResponse';
import { BloominCardFields } from './BloominCardFields';
import {
  digitsOnly,
  initialBloominCardForm,
  normalizeExpiryYear,
  type BloominCardFieldErrors,
  type BloominCardFormState,
} from './bloominRegistration';
import { detectCardBrand, isExpiryInFuture, isValidCvv, isValidLuhn } from '@/utils/cardValidation';

/**
 * 검증을 통과한 카드 정규화 payload — `onSubmitOverride` 에 전달.
 *
 * 후불 "지금 결제"(C3 #590) 처럼 initiate POST 대신 다른 endpoint(`/charge`)로
 * 제출해야 할 때, 카드 입력 UI/검증/PCI 메모리 폐기 로직을 재사용하면서
 * 제출 동작만 호출자가 주입하기 위한 계약.
 *
 * `ChargePaymentRequest` 와 동형 (`tarjeta`/`mes`/`anio`/`digitos`/`titular`).
 */
export interface BloominCardSubmitPayload {
  /** 카드 번호 (숫자만) */
  tarjeta: string;
  /** 만료 월 (2자리, 예: "03") */
  mes: string;
  /** 만료 연도 (4자리, 예: "2030") */
  anio: string;
  /** CVV (3~4자리) */
  digitos: string;
  /** 카드 소유자명 (대문자) */
  titular: string;
}

interface BloominCardInputFormProps {
  /**
   * admin BE 가 반환한 주문/결제 기반 initiate URL.
   *
   * `onSubmitOverride` 가 주어지면 사용되지 않는다(제출을 호출자가 담당).
   */
  initiateUrl: string;
  /** 비회원 주문 조회 코드 */
  guestPassword?: string;
  /** 결제 성공 시 callback (기본 initiate 흐름 전용) */
  onSuccess: (result: BloominPaymentResult) => void;
  /** 결제 실패 시 callback */
  onError: (error: string) => void;
  /** 결제 진행 중 disable */
  isSubmitting?: boolean;
  /** i18n labels (호출자 주입) */
  labels: BloominCardInputLabels;
  /**
   * 제출 동작 override (C3 #590 — 후불 "지금 결제" 재사용).
   *
   * 주어지면 내부 initiate POST 를 건너뛰고, 검증 통과 + 카드 메모리 폐기 후
   * 정규화 payload 를 전달한다. 호출자가 `/charge` 등 다른 흐름을 수행한다.
   * (값 미주입 시 기존 initiate POST 동작 — 하위 호환).
   */
  onSubmitOverride?: (payload: BloominCardSubmitPayload) => Promise<void>;
}

export interface BloominPaymentResult {
  payment_id: number;
  transaccionId: string;
  challengeUrl: string;
  reference3D?: string | null;
}

export interface BloominCardInputLabels {
  cardNumberLabel: string;
  cardNumberPlaceholder: string;
  expiryMonthLabel: string;
  expiryYearLabel: string;
  cvvLabel: string;
  cvvPlaceholder: string;
  holderNameLabel: string;
  holderNamePlaceholder: string;
  submitButton: string;
  securityNotice: string;
  errors: {
    cardNumberInvalid: string;
    expiryInvalid: string;
    cvvInvalid: string;
    holderNameRequired: string;
  };
}

const INPUT_ERROR_FIELD: Record<keyof BloominCardFormState, keyof BloominCardFieldErrors> = {
  cardNumber: 'cardNumber',
  expiryMonth: 'expiry',
  expiryYear: 'expiry',
  cvv: 'cvv',
  holder: 'holder',
};

export function BloominCardInputForm({
  initiateUrl,
  guestPassword,
  onSuccess,
  onError: notifyError,
  isSubmitting = false,
  labels,
  onSubmitOverride,
}: BloominCardInputFormProps) {
  const onError = useCallback((message: string, details: { status: number; code?: string } = { status: 0, code: 'CLIENT_ERROR' }) => {
    // One telemetry owner for every form failure; never attach response/card bodies.
    logger.error('[Bloomin] payment initiate failed', details);
    notifyError(message);
  }, [notifyError]);

  // 카드 정보 — useState 로 관리하다 form 제출 후 즉시 초기화 (PCI DSS 정합).
  const [card, setCard] = useState<BloominCardFormState>(initialBloominCardForm);
  const [fieldErrors, setFieldErrors] = useState<BloominCardFieldErrors>({});
  const [isProcessing, setIsProcessing] = useState(false);

  const clearFieldError = useCallback((field: keyof BloominCardFormState) => {
    const errorField = INPUT_ERROR_FIELD[field];
    setFieldErrors((prev) => {
      if (!prev[errorField]) return prev;
      const next = { ...prev };
      delete next[errorField];
      return next;
    });
  }, []);

  const validate = useCallback((): BloominCardFieldErrors => {
    const errors: BloominCardFieldErrors = {};

    // 카드 번호 — 13~19자리 숫자 + Luhn 체크섬
    const cardNumberClean = digitsOnly(card.cardNumber);
    if (!/^\d{13,19}$/.test(cardNumberClean) || !isValidLuhn(cardNumberClean)) {
      errors.cardNumber = labels.errors.cardNumberInvalid;
    }

    // 만료일 — 미래(당월 포함) + 비현실적 미래(+20년) 차단.
    //   연도는 등록 경로(bloominRegistration)와 동일하게 2자리(YY)→20YY 정규화.
    const month = Number(digitsOnly(card.expiryMonth));
    const year = normalizeExpiryYear(card.expiryYear);
    const maxYear = new Date().getFullYear() + 20;
    if (!isExpiryInFuture(month, year) || year > maxYear) {
      errors.expiry = labels.errors.expiryInvalid;
    }

    // CVV — brand 별 정확 길이 (visa/mc 3 / amex 4 / unknown 3~4)
    const cvv = digitsOnly(card.cvv);
    if (!isValidCvv(cvv, detectCardBrand(cardNumberClean))) {
      errors.cvv = labels.errors.cvvInvalid;
    }

    // 소유자 이름
    if (card.holder.trim() === '') {
      errors.holder = labels.errors.holderNameRequired;
    }

    return errors;
  }, [card, labels]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      const errors = validate();
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }

      setIsProcessing(true);

      // C3 #590 재사용 분기: onSubmitOverride 주입 시 initiate POST 대신 정규화
      //   payload 를 호출자에게 위임(예: 후불 "지금 결제" → /charge).
      //   PCI DSS 정합: payload capture 직후 + override 호출 *전* 카드 메모리 폐기.
      if (onSubmitOverride) {
        const overridePayload: BloominCardSubmitPayload = {
          tarjeta: digitsOnly(card.cardNumber),
          mes: digitsOnly(card.expiryMonth).padStart(2, '0'),
          anio: String(normalizeExpiryYear(card.expiryYear)),
          digitos: digitsOnly(card.cvv),
          titular: card.holder.trim().toUpperCase(),
        };

        // 카드 정보 즉시 폐기 (FE 메모리 잔존 차단, override 호출 전).
        setCard(initialBloominCardForm);

        try {
          await onSubmitOverride(overridePayload);
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Payment failed';
          onError(errorMsg, { status: 0, code: 'OVERRIDE_FAILED' });
        } finally {
          setIsProcessing(false);
        }

        return;
      }

      // ⚠️ C1 시정 (code-reviewer 1R, PCI DSS P0):
      //   payload 직렬화 직후 + fetch 호출 *전* setCard(initialBloominCardForm) 즉시 실행.
      //   await 동안 React state 메모리 잔존 차단 (devtools / heap dump / extension 노출 회피).
      //   fetch body 는 이미 capture 된 immutable string 사용.
      const apiEndpoint = import.meta.env.VITE_API_ENDPOINT ?? '';

      // I2 시정: VITE_API_ENDPOINT 미설정 시 명시적 차단 (silent fail-safe).
      if (apiEndpoint === '') {
        setIsProcessing(false);
        onError('VITE_API_ENDPOINT not configured');
        return;
      }

      // codex 우산 audit P0 시정: HTTPS 런타임 강제 — PAN/CVV 평문 송신 회피.
      // codex 우산 2R P1 시정: URL 파싱으로 prefix 가드 우회 차단.
      //   `http://localhost.evil.com` 등 startsWith 우회 → URL.hostname 정확 매칭.
      let parsedEndpoint: URL;
      try {
        parsedEndpoint = new URL(apiEndpoint);
      } catch {
        setIsProcessing(false);
        onError('VITE_API_ENDPOINT is not a valid URL');
        return;
      }
      const isHttps = parsedEndpoint.protocol === 'https:';
      const isLocalDev =
        parsedEndpoint.protocol === 'http:' &&
        (parsedEndpoint.hostname === 'localhost' || parsedEndpoint.hostname === '127.0.0.1');
      if (!isHttps && !isLocalDev) {
        setIsProcessing(false);
        onError('VITE_API_ENDPOINT must use HTTPS (TLS 1.2+) for PCI DSS compliance');
        return;
      }

      let initiateEndpoint: URL;
      try {
        initiateEndpoint = new URL(initiateUrl, parsedEndpoint);
      } catch {
        setIsProcessing(false);
        onError('Bloomin initiate URL is not valid');
        return;
      }

      const isInitiateHttps = initiateEndpoint.protocol === 'https:';
      const isInitiateLocalDev =
        initiateEndpoint.protocol === 'http:' &&
        (initiateEndpoint.hostname === 'localhost' || initiateEndpoint.hostname === '127.0.0.1');
      if (!isInitiateHttps && !isInitiateLocalDev) {
        setIsProcessing(false);
        onError('Bloomin initiate URL must use HTTPS (TLS 1.2+)');
        return;
      }

      if (initiateEndpoint.origin !== parsedEndpoint.origin) {
        setIsProcessing(false);
        onError('Bloomin initiate URL must target the configured API origin');
        return;
      }

      const requestBody = JSON.stringify({
        ...(guestPassword ? { guest_password: guestPassword } : {}),
        tarjeta: digitsOnly(card.cardNumber),
        mes: digitsOnly(card.expiryMonth).padStart(2, '0'),
        anio: String(normalizeExpiryYear(card.expiryYear)),
        digitos: digitsOnly(card.cvv),
        titular: card.holder.trim().toUpperCase(),
      });

      // 카드 정보 즉시 폐기 (PCI DSS — FE 메모리 잔존 차단, fetch 호출 전).
      setCard(initialBloominCardForm);

      try {
        const response = await fetch(initiateEndpoint.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Accept-Language': getApiErrorLanguage(),
          },
          credentials: 'include',
          body: requestBody,
        });

        const result = await readBloominInitiateResponse(response);
        onSuccess(result);
      } catch (err) {
        const errorMsg = err instanceof BloominInitiateError
          ? err.message
          : err instanceof TypeError
            ? getStatusMessage(0) ?? getGenericFallbackMessage()
            : getGenericFallbackMessage();
        // Report once, without provider bodies, reflected card data or raw errors.
        onError(errorMsg, {
          status: err instanceof BloominInitiateError ? err.status : 0,
          code: err instanceof BloominInitiateError ? err.code : 'REQUEST_FAILED',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [card, guestPassword, initiateUrl, onSuccess, onError, validate, onSubmitOverride],
  );

  const disabled = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-[16px]" noValidate>
      <BloominCardFields
        value={card}
        onChange={setCard}
        onFieldChange={clearFieldError}
        fieldErrors={fieldErrors}
        labels={labels}
        idPrefix="bloomin-card"
        brandTestId="bloomin-card-brand"
        disabled={disabled}
      />

      <div className="flex items-center gap-[8px] text-[12px] text-muted-foreground">
        <Lock className="w-[14px] h-[14px]" />
        <span>{labels.securityNotice}</span>
      </div>

      <Button type="submit" disabled={disabled} className="w-full h-[52px] text-[16px] font-bold">
        {isProcessing ? <Loader2 className="w-[20px] h-[20px] animate-spin" /> : labels.submitButton}
      </Button>
    </form>
  );
}
